import React from "react";

/*
 * One place for the sidebar menu, sub-admin permissions and page protection.
 * Every page has its own unique key. A sub-admin can open a page only if its key
 * is in their access list. "system_user" is for the main owner only.
 */

export const links = [
  {
    links: [
      { name: "dashboard", title: "Dashboard", key: "dashboard", icon: <i className="fa-solid fa-gauge-high"></i>, desc: "Overview numbers (always visible)" },
      { name: "report", title: "User Reports", key: "report_request", icon: <i className="fa-solid fa-user-lock"></i>, desc: "See user reports, remove reports" },
      { name: "app-ads", title: "App Ads", key: "app_ads", icon: <i className="fa-solid fa-panorama"></i>, desc: "Upload and delete in-app ad banners" },
      { name: "owner-post", title: "Owner Post", key: "owner_post", icon: <i className="fa-solid fa-check-to-slot"></i>, desc: "Create posts that notify every user", risk: "broadcast" },
    ],
  },
  {
    title: "App Users",
    links: [
      { name: "users/all", title: "All Users", key: "all_users", icon: <i className="fa-solid fa-users"></i>, desc: "View users, block and delete accounts", risk: "sensitive" },
      { name: "users/creator", title: "Creator List", key: "creator_list", icon: <i className="fa-solid fa-user"></i>, desc: "Creators and karma points" },
      { name: "users/admin", title: "Admin List", key: "admin_list", icon: <i className="fa-solid fa-user-tie"></i>, desc: "App admins and their details", risk: "sensitive" },
      { name: "admin-req", title: "Admin Request", key: "admin_request", icon: <i className="fa-solid fa-user-plus"></i>, desc: "Check KYC documents, approve admins", risk: "sensitive" },
      { name: "user/message-reports", title: "Message Reports", key: "user_message_report", icon: <i className="fas fa-flag"></i>, desc: "Reported private messages" },
    ],
  },
  {
    title: "Owner Links",
    links: [{ name: "owner-link", title: "Owner Links", key: "owner_links", icon: <i className="fa-solid fa-link"></i>, desc: "Links pushed to all users" }],
  },
  {
    title: "Videos",
    links: [
      { name: "youtube", title: "YouTube", key: "youtube", icon: <i className="fa-brands fa-youtube"></i>, desc: "YouTube overview" },
      { name: "videos/youtube-video", title: "YouTube Videos", key: "youtube_videos", icon: <i className="fa-brands fa-youtube"></i>, desc: "Manage YouTube videos" },
      { name: "videos/youtube-short", title: "YouTube Shorts", key: "youtube_shorts", icon: <i className="fa-brands fa-youtube"></i>, desc: "Manage YouTube shorts" },
      { name: "videos/instagram-reel", title: "Instagram Reels", key: "instagram_reels", icon: <i className="fa-brands fa-instagram"></i>, desc: "Manage Instagram reels" },
      { name: "videos/facebook-video", title: "Facebook Videos", key: "facebook_videos", icon: <i className="fa-brands fa-square-facebook"></i>, desc: "Manage Facebook videos" },
      { name: "videos/facebook-reel", title: "Facebook Reels", key: "facebook_reels", icon: <i className="fa-brands fa-square-facebook"></i>, desc: "Manage Facebook reels" },
    ],
  },
  {
    title: "Groups",
    links: [
      { name: "group/list", title: "All Groups", key: "group_list", icon: <i className="fas fa-users"></i>, desc: "Groups, members and group chat" },
      { name: "group/join-request", title: "Joining Requests", key: "group_joining_req", icon: <i className="fas fa-user-plus"></i>, desc: "Accept paid joins (pays commission)", risk: "money" },
      { name: "group/wallet", title: "Member Wallets", key: "wallet_list", icon: <i className="fas fa-wallet"></i>, desc: "Wallet balances of members", risk: "money" },
      { name: "wallet/withdrawal-req", title: "Withdrawals", key: "withdrawal_request", icon: <i className="fa-solid fa-money-bill-trend-up"></i>, desc: "Approve payouts to users", risk: "money" },
      { name: "group-packages", title: "Group Packages", key: "group_package_config", icon: <i className="fa-regular fa-circle-check"></i>, desc: "Group package prices and commission", risk: "money" },
      { name: "group/all-members", title: "All Group Members", key: "all_group_members_list", icon: <i className="fas fa-users"></i>, desc: "Block or remove members in groups" },
      { name: "group/message-reports", title: "Group Msg Reports", key: "group_message_report", icon: <i className="fas fa-flag"></i>, desc: "Reported group messages" },
    ],
  },
  {
    title: "Ads Reward",
    links: [{ name: "ads-reward-wallet", title: "Ads Reward Wallet", key: "ads_reward_wallet", icon: <i className="fas fa-wallet"></i>, desc: "Ad reward balances", risk: "money" }],
  },
  {
    title: "Customer",
    links: [
      { name: "customer/list", title: "All Customers", key: "customer_list", icon: <i className="fa-solid fa-user-check"></i>, desc: "Customers, monetization, delete" },
      { name: "customer/request", title: "Customer Request", key: "customer_request", icon: <i className="fa-solid fa-user-plus"></i>, desc: "Accept paid customers (pays commission)", risk: "money" },
      { name: "customer-packages", title: "Customer Packages", key: "customer_packages", icon: <i className="fa-solid fa-box"></i>, desc: "Customer package prices", risk: "money" },
    ],
  },
  {
    title: "Monetization",
    links: [
      { name: "customer/link/active/youtube", title: "YouTube Links", key: "active_youtube_links", icon: <i className="fa-brands fa-youtube"></i>, desc: "Start or pause YouTube customer links" },
      { name: "customer/link/active/facebook", title: "Facebook Links", key: "active_facebook_links", icon: <i className="fa-brands fa-facebook"></i>, desc: "Start or pause Facebook customer links" },
    ],
  },
  {
    title: "App Updates",
    links: [{ name: "app-updates", title: "App Updates", key: "app_updates", icon: <i className="fa-solid fa-mobile-screen-button"></i>, desc: "Publish app versions to all phones", risk: "broadcast" }],
  },
  {
    title: "Website",
    links: [{ name: "website", title: "Website Control", key: "website_control", icon: <i className="fa-solid fa-globe"></i>, desc: "ycnp.in website data", ownerOnly: true }],
  },
  {
    title: "System Users",
    links: [{ name: "system/user", title: "Managers", key: "system_user", icon: <i className="fa-solid fa-users"></i>, desc: "Only the main owner", ownerOnly: true }],
  },
];

// Pages that can be given to a sub-admin, grouped like the sidebar
export const PERMISSION_GROUPS = links
  .map((s) => ({ title: s.title || "General", items: s.links.filter((l) => l.key !== "dashboard" && !l.ownerOnly) }))
  .filter((g) => g.items.length);

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key));

export const PERMISSION_BY_KEY = Object.fromEntries(PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => [i.key, { ...i, group: g.title }])));

// Old panels gave one key to several pages; these keys now open the new split pages too
export const LEGACY_KEYS = {
  group_joining_req: ["customer_request", "active_youtube_links", "active_facebook_links"],
};

export const expandAccess = (list) => {
  const out = new Set(Array.isArray(list) ? list : []);
  Object.entries(LEGACY_KEYS).forEach(([oldKey]) => {
    // only expand for accounts saved before the split (they have no new keys at all)
    if (out.has(oldKey) && !["customer_request", "active_youtube_links", "active_facebook_links"].some((k) => out.has(k))) {
      LEGACY_KEYS[oldKey].forEach((k) => out.add(k));
    }
  });
  return Array.from(out);
};

// Which permission opens which address. First match wins.
export const ROUTE_RULES = [
  { test: (p) => p === "/" || p === "/dashboard", keys: ["dashboard"] },
  { test: (p) => p === "/report", keys: ["report_request"] },
  { test: (p) => p === "/app-ads", keys: ["app_ads"] },
  { test: (p) => p === "/owner-post", keys: ["owner_post"] },
  { test: (p) => p === "/users/all", keys: ["all_users"] },
  { test: (p) => p === "/users/creator", keys: ["creator_list"] },
  { test: (p) => p === "/users/admin", keys: ["admin_list"] },
  { test: (p) => p === "/admin-req", keys: ["admin_request"] },
  { test: (p) => p === "/user/message-reports", keys: ["user_message_report"] },
  { test: (p) => p.startsWith("/user-profile/"), keys: ["all_users", "creator_list", "admin_list", "admin_request"] },
  { test: (p) => p === "/owner-link", keys: ["owner_links"] },
  { test: (p) => p === "/youtube", keys: ["youtube"] },
  { test: (p) => p === "/videos/youtube-video", keys: ["youtube_videos"] },
  { test: (p) => p === "/videos/youtube-short", keys: ["youtube_shorts"] },
  { test: (p) => p === "/videos/instagram-reel", keys: ["instagram_reels"] },
  { test: (p) => p === "/videos/facebook-video", keys: ["facebook_videos"] },
  { test: (p) => p === "/videos/facebook-reel", keys: ["facebook_reels"] },
  { test: (p) => p === "/group/list", keys: ["group_list"] },
  { test: (p) => p === "/group/join-request", keys: ["group_joining_req"] },
  { test: (p) => p === "/group/wallet", keys: ["wallet_list"] },
  { test: (p) => p === "/wallet/withdrawal-req" || p === "/wallet/withdrawal" || p === "/wallet/withdrawal-cancel", keys: ["withdrawal_request"] },
  { test: (p) => p.startsWith("/wallet-account/"), keys: ["wallet_list", "withdrawal_request"] },
  { test: (p) => p === "/group-packages", keys: ["group_package_config"] },
  { test: (p) => p === "/group/all-members", keys: ["all_group_members_list"] },
  { test: (p) => p === "/group/message-reports", keys: ["group_message_report"] },
  { test: (p) => p.startsWith("/group-chat/") || p.startsWith("/group/members/"), keys: ["group_list", "all_group_members_list", "group_message_report"] },
  { test: (p) => p.startsWith("/ads-reward-wallet-account/"), keys: ["ads_reward_wallet"] },
  { test: (p) => p === "/ads-reward-wallet", keys: ["ads_reward_wallet"] },
  { test: (p) => p === "/customer/list", keys: ["customer_list"] },
  { test: (p) => p === "/customer/request", keys: ["customer_request"] },
  { test: (p) => p === "/customer-packages", keys: ["customer_packages"] },
  { test: (p) => p === "/customer/link/active/youtube", keys: ["active_youtube_links"] },
  { test: (p) => p === "/customer/link/active/facebook", keys: ["active_facebook_links"] },
  { test: (p) => p.startsWith("/customer/link/"), keys: ["customer_list", "customer_request"] },
  { test: (p) => p === "/app-updates", keys: ["app_updates"] },
  { test: (p) => p === "/system/user", keys: ["__owner_only__"] },
  { test: (p) => p === "/website", keys: ["__owner_only__"] },
];

// Can this sub-admin open this address?
export const canOpen = (path, access) => {
  const p = (path || "/").replace(/\/+$/, "") || "/";
  const rule = ROUTE_RULES.find((r) => r.test(p));
  if (!rule) return false; // unknown pages are closed for sub-admins
  if (rule.keys.includes("dashboard")) return true;
  return rule.keys.some((k) => access.includes(k));
};